import {ActivityIndicator, Image, Pressable, View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Text} from '#/components/Typography'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {ACTIVITY_STATUS_COLORS, useTrendLabels} from '../data/labels'
import {type Trend} from '../data/trends'
import {useTrendPosts} from '../data/useTrendPosts'
import {
  getVoicesDisplay,
  useImageLoadFailed,
  useTrendSearchPress,
} from '../util'
import {FullPostPreview} from './FullPostPreview'
import {TrendComposerPill} from './TrendComposerPrompt'
import {TrendHeroBadges} from './TrendHeroBadges'
import {TrendRankHeader} from './TrendRankPill'

/**
 * Hero prominence trend card.
 *
 * Mirrors Figma node `807:5960` in `yYh1MBotDu5Gm1KpL8EZxH`
 * (W Social — Product — Trends). Used when `section.cardStyle === 'hero'`.
 *
 * Currently renders: category/activity-status row, title + description,
 * active-voices row with avatar stack, three metric pills, hero image, the
 * two inline top posts (rendered as `FullPostPreview` — Figma `384:53798` in
 * `C8J3bA4ThNVCG0vzlHeI16` — with image embeds and display-only
 * reply/repost/like counts), and the "Explore trend" CTA.
 *
 * The third metric pill ("Data Label" in Figma) is intentionally omitted
 * until design clarifies what value goes there — see the open question in
 * `.agents/repos/root/trends/trends-how-it-works.md`.
 */
export function HeroTrendCard({
  trend,
  rankLabel,
}: {
  trend: Trend
  /** When set (Top trends block), the top row shows the green rank pill +
   *  category instead of category · activity status. */
  rankLabel?: string
}) {
  const heroImage = useImageLoadFailed(trend.heroImage?.url)
  const t = useTheme()
  const ss = useSemanticTokens()
  const {_} = useLingui()
  const goToTrendSearch = useTrendSearchPress()
  const {getCategoryLabel, activityStatusLabels} = useTrendLabels()

  const activityStatusColor = ACTIVITY_STATUS_COLORS[trend.activityStatus]
  // Undefined when the backend sends a status we have no label for — the
  // row then renders the category alone (no separator dot).
  const activityStatusLabel = activityStatusLabels[trend.activityStatus] as
    | string
    | undefined
  const visibleAvatars = trend.participantAvatars.slice(0, 5)
  const voices = getVoicesDisplay(trend.metrics.activeVoicesCount)
  // Posts power the bottom of the card: a list + "Explore trend" when there are
  // posts, or the "Start the discussion" composer when there are none.
  const {posts, isLoading: postsLoading} = useTrendPosts(trend, {
    limit: 2,
    sort: 'top',
  })

  return (
    <View style={[a.w_full, a.gap_xl, a.px_lg, a.py_4xl]}>
      {/* Title + metadata + hero image are one press target → trend search.
          The inline posts and the Explore button below are separate targets. */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={_(msg`Explore trend: ${trend.title}`)}
        accessibilityHint={_(msg`Searches for this trend`)}
        onPress={() => goToTrendSearch(trend)}
        style={[a.gap_xl]}>
        {/* Heading block: rank pill · category (Top trends) OR category ·
            activity status, then title, description, voices */}
        <View style={[a.w_full, a.gap_md]}>
          {rankLabel ? (
            <TrendRankHeader
              rankLabel={rankLabel}
              categoryLabel={getCategoryLabel(trend.category)}
            />
          ) : (
            <View style={[a.flex_row, a.align_center, a.gap_xs]}>
              <Text
                style={[a.text_xs, a.font_bold, t.atoms.text_contrast_medium]}>
                {getCategoryLabel(trend.category)}
              </Text>
              {/* Dot + status render together — no dangling dot when the
                  backend sends a status we have no label for. */}
              {!!activityStatusLabel && (
                <>
                  <View
                    style={[
                      a.rounded_full,
                      {
                        width: 4,
                        height: 4,
                        backgroundColor: t.atoms.text_contrast_medium.color,
                        opacity: 0.97,
                      },
                    ]}
                  />
                  <Text
                    style={[
                      a.text_xs,
                      a.font_medium,
                      {color: activityStatusColor},
                    ]}>
                    {activityStatusLabel}
                  </Text>
                </>
              )}
            </View>
          )}

          <View style={[a.gap_sm]}>
            <Text style={[a.text_2xl, a.font_bold, t.atoms.text]}>
              {trend.title}
            </Text>
            <Text
              style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
              {trend.description}
            </Text>
          </View>

          {voices.showVoices && (
            <View style={[a.flex_row, a.align_center, a.gap_sm]}>
              <AvatarStack avatars={visibleAvatars} />
              <Text
                style={[
                  a.text_xs,
                  a.font_medium,
                  t.atoms.text_contrast_medium,
                ]}>
                {voices.showCount &&
                  `${formatCount(trend.metrics.activeVoicesCount ?? 0)} `}
                {_(msg`active voices`)}
              </Text>
            </View>
          )}
        </View>

        {/* New velocity + signal badges only; self-hides when the trend has
            neither. Legacy growth / posts-per-hour pills are intentionally
            never shown. */}
        <View style={[a.w_full, a.gap_md]}>
          <TrendHeroBadges trend={trend} />

          {/* Image is shown only when present — no placeholder box when the
              trend has no image. */}
          {trend.heroImage && !heroImage.failed && (
            // The trend title sits directly above this image, so it's
            // decorative for screen readers — no label/hint needed.
            <Image
              accessibilityIgnoresInvertColors
              source={{uri: trend.heroImage.url}}
              onError={heroImage.onError}
              style={[
                a.w_full,
                a.rounded_md,
                {
                  // Same ratio as the small carousel trend cards (176×92).
                  aspectRatio: 176 / 92,
                  backgroundColor: ss.bg.contrast_25,
                },
              ]}
              resizeMode="cover"
            />
          )}
        </View>
      </Pressable>

      {postsLoading ? (
        <PostsPlaceholder />
      ) : posts.length > 0 ? (
        <>
          <View style={[a.w_full, a.gap_md]}>
            {posts.map(post => (
              <FullPostPreview key={post.uri} post={post} />
            ))}
          </View>
          <Button
            label={_(msg`Explore trend: ${trend.title}`)}
            color="secondary_inverted"
            size="large"
            variant="solid"
            shape="default"
            onPress={() => goToTrendSearch(trend)}>
            <ButtonText>
              <Trans>Explore trend</Trans>
            </ButtonText>
          </Button>
        </>
      ) : (
        // No posts yet → invite the user to start the discussion in place of the
        // empty-state box + "Explore trend" button.
        <TrendComposerPill
          trend={trend}
          headingText={<Trans>Start the discussion</Trans>}
        />
      )}
    </View>
  )
}

function PostsPlaceholder() {
  const ss = useSemanticTokens()
  return (
    <View
      style={[
        a.w_full,
        a.align_center,
        a.justify_center,
        a.p_lg,
        a.rounded_md,
        {minHeight: 88, backgroundColor: ss.bg.contrast_25},
      ]}>
      <ActivityIndicator />
    </View>
  )
}

function AvatarStack({
  avatars,
}: {
  avatars: {did: string; handle: string; avatarUrl: string}[]
}) {
  const ss = useSemanticTokens()
  return (
    <View style={[a.flex_row, a.align_center]}>
      {avatars.map((avatar, i) => (
        <Image
          key={avatar.did}
          accessibilityIgnoresInvertColors
          source={{uri: avatar.avatarUrl}}
          style={[
            a.rounded_full,
            {
              width: 24,
              height: 24,
              marginLeft: i === 0 ? 0 : -8,
              borderWidth: 1,
              borderColor: 'rgba(0,0,0,0.2)',
              backgroundColor: ss.bg.contrast_25,
            },
          ]}
        />
      ))}
    </View>
  )
}

/* ---------- formatting + labels ---------- */

function formatCount(n: number): string {
  // Non-breaking space matches the Figma's number formatting ("2 847").
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
}
